import { ArrowRight, PlayCircle } from 'lucide-react';

export function Hero() {
  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16"
      aria-labelledby="hero-heading"
    >
      {/* Background gradient */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-indigo-950/40 via-gray-950 to-gray-950"
        aria-hidden="true"
      />
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-indigo-500/10 rounded-full blur-3xl"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-4 py-1.5 mb-8">
            <span className="h-2 w-2 rounded-full bg-indigo-400 animate-pulse" aria-hidden="true" />
            <span className="text-sm font-medium text-indigo-300">Now in Private Beta</span>
          </div>

          {/* Headline */}
          <h1
            id="hero-heading"
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight"
          >
            <span className="text-white">The </span>
            <span className="gradient-text">Kubernetes</span>
            <span className="text-white"> of Enterprise AI</span>
          </h1>

          {/* Subheading */}
          <p className="mt-6 text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Opair is the governance layer that sits between your applications and AI providers.
            Control costs, enforce policies, detect sensitive data, and maintain compliance — all
            without changing a single line of application code.
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#demo"
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-3 text-base font-semibold text-white hover:bg-indigo-500 transition-all hover:shadow-lg hover:shadow-indigo-500/20"
            >
              Request Demo
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <a
              href="#architecture"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-900/50 px-6 py-3 text-base font-semibold text-gray-300 hover:text-white hover:border-gray-600 transition-all"
            >
              <PlayCircle className="h-4 w-4" aria-hidden="true" />
              View Architecture
            </a>
          </div>
        </div>

        {/* Animated Architecture Diagram */}
        <div
          className="mt-20 flex justify-center"
          aria-label="Architecture overview showing Control Plane between applications and AI providers"
        >
          <HeroArchDiagram />
        </div>
      </div>
    </section>
  );
}

/** Simplified animated SVG showing data flow: Apps → Gateway → AI Providers */
function HeroArchDiagram() {
  return (
    <svg
      viewBox="0 0 800 280"
      className="w-full max-w-3xl"
      role="img"
      aria-label="Architecture diagram: Applications connect through the Opair Gateway to AI Providers, governed by the Control Plane"
    >
      {/* Connection lines */}
      <line
        x1="185"
        y1="80"
        x2="295"
        y2="80"
        stroke="#4f46e5"
        strokeWidth="2"
        strokeDasharray="6 4"
        opacity="0.5"
      />
      <line
        x1="505"
        y1="80"
        x2="615"
        y2="80"
        stroke="#4f46e5"
        strokeWidth="2"
        strokeDasharray="6 4"
        opacity="0.5"
      />
      <line
        x1="400"
        y1="120"
        x2="400"
        y2="180"
        stroke="#6366f1"
        strokeWidth="2"
        strokeDasharray="6 4"
        opacity="0.5"
      />

      {/* Flow dots - left to center */}
      <circle r="4" fill="#818cf8" className="animate-flow-right">
        <animateMotion dur="2s" repeatCount="indefinite" path="M185,80 L295,80" />
      </circle>
      {/* Flow dots - center to right */}
      <circle r="4" fill="#818cf8" className="animate-flow-right">
        <animateMotion dur="2s" repeatCount="indefinite" path="M505,80 L615,80" begin="0.5s" />
      </circle>
      {/* Flow dots - vertical */}
      <circle r="4" fill="#a5b4fc" className="animate-flow-down">
        <animateMotion dur="2.5s" repeatCount="indefinite" path="M400,120 L400,180" begin="0.3s" />
      </circle>

      {/* Apps box */}
      <rect
        x="40"
        y="45"
        width="145"
        height="70"
        rx="12"
        fill="#1e1b4b"
        stroke="#4338ca"
        strokeWidth="1.5"
      />
      <text x="112" y="75" textAnchor="middle" fill="#c7d2fe" fontSize="11" fontWeight="600">
        Your Apps
      </text>
      <text x="112" y="95" textAnchor="middle" fill="#818cf8" fontSize="10">
        SaaS · Internal · APIs
      </text>

      {/* Gateway / Opair box */}
      <rect
        x="295"
        y="40"
        width="210"
        height="80"
        rx="14"
        fill="#312e81"
        stroke="#6366f1"
        strokeWidth="2"
        className="glow-indigo"
      />
      <text x="400" y="72" textAnchor="middle" fill="#e0e7ff" fontSize="13" fontWeight="700">
        Opair Gateway
      </text>
      <text x="400" y="92" textAnchor="middle" fill="#a5b4fc" fontSize="10">
        Policy · Detect · Route · Audit
      </text>

      {/* AI Providers box */}
      <rect
        x="615"
        y="45"
        width="145"
        height="70"
        rx="12"
        fill="#1e1b4b"
        stroke="#4338ca"
        strokeWidth="1.5"
      />
      <text x="687" y="75" textAnchor="middle" fill="#c7d2fe" fontSize="11" fontWeight="600">
        AI Providers
      </text>
      <text x="687" y="95" textAnchor="middle" fill="#818cf8" fontSize="10">
        OpenAI · Anthropic · Azure · Vertex AI
      </text>

      {/* Control Plane box */}
      <rect
        x="300"
        y="185"
        width="200"
        height="65"
        rx="12"
        fill="#1e1b4b"
        stroke="#4338ca"
        strokeWidth="1.5"
      />
      <text x="400" y="213" textAnchor="middle" fill="#c7d2fe" fontSize="11" fontWeight="600">
        Control Plane
      </text>
      <text x="400" y="233" textAnchor="middle" fill="#818cf8" fontSize="10">
        Policies · Config · Analytics
      </text>

      {/* Arrow labels */}
      <text x="240" y="70" textAnchor="middle" fill="#6366f1" fontSize="9" fontWeight="500">
        Request
      </text>
      <text x="560" y="70" textAnchor="middle" fill="#6366f1" fontSize="9" fontWeight="500">
        Forward
      </text>
      <text x="435" y="155" textAnchor="start" fill="#6366f1" fontSize="9" fontWeight="500">
        Govern
      </text>
    </svg>
  );
}
